/* eslint-disable @typescript-eslint/no-explicit-any */
import { reqGetGameList } from "@/api/clientApi/front";
import {
  GetUserPayChannelV2,
  GetUserPayUrlV2,
  PayPageOpenStatus,
  UserApplyPayV2,
} from "@/api/clientApi/wallet.ts";
import { toast } from "@/components/Base/Toaster/Toaster.tsx";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { ResConfig } from "@/constants/resConfig";
import { noEntryGameList } from "@/enum/game.ts";
import { DepositKeyTypeEnum } from "@/enum/wallet.ts";
import { useLiveChat } from "@/hooks/livechat/useLiveChat.ts";
import { useRouter } from "@/hooks/useRouter";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile.ts";
import { clickShare, handleOutLink, toOutLink } from "@/lib/utils";
import { getShareListCfg, getWebsiteConfigV2 } from "@/stores/configStore.ts";
import { KYCStore } from "@/stores/kycStore.ts";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import { userStore } from "@/stores/userStore.ts";
import {
  ActivityTypeEnum,
  ClickType,
  UserKycAuthActionType,
  ipatypeEnum,
} from "@/types/compontent/base/baseEnum.ts";

// 前端自定义点击事件 1-跳转外部链接 2-跳转页面 3-跳转弹窗 4-进入游戏 5-live-chat客服 6-跳转内部文档 7-页面滚动 8-无徐配置打开内部iframe
export function useClick() {
  const { push, preCheckEnterGame, toCasino, toDeposit, handleNoLogin } =
    useRouter();
  const click = async (
    click_id: number,
    click_params: string | object | number,
    extra?: {
      callback?: () => void;
    },
  ) => {
    if (!click_id) return;
    const { showDialog } = useDialog();
    const { clickLiveChat } = useLiveChat();

    const validateByRule = (rule, data) => {
      const errors = [];

      // identify_types：只要任意一个有值即可
      if (Array.isArray(rule.identify_types)) {
        const identifys = Array.isArray(data.identifys) ? data.identifys : [];

        const hasOneValid = rule.identify_types.some((type) => {
          return identifys.some((i) => i.identify_type === type && i.identify);
        });

        if (!hasOneValid) {
          errors.push(`identify_types 中至少需要一个有效值`);
        }
      }

      // need_xxx → pay_attach.xxx 必填校验
      Object.keys(rule).forEach((key) => {
        if (key.startsWith("need_") && rule[key] === 1) {
          const field = key.replace("need_", "");
          const value = data[field];

          if (value === null || value === undefined || value === "") {
            errors.push(`${field} 为必填`);
          }
        }
      });

      return {
        pass: errors.length === 0,
        errors,
      };
    };

    const resolveDepositResponse = (res) => {
      if (res?.url.includes("http")) {
        if ((res as any)?.jump_type && (res as any)?.jump_type === 1) {
          toOutLink(res?.url);
        } else {
          showDialog({
            type: DialogType.DepositIframe,
            data: {
              iframeUrl: res?.url,
              countDownTime: res?.count_down_time,
            },
          });
        }
      } else {
        showDialog({
          type: DialogType.DepositPayment,
          data: {
            ...res,
            amount: res?.amount,
            methodUrl: res?.icon,
          },
        });
      }
    };

    const repeatDeposit = async (
      order_no: string,
      count: number = 0,
      extraPrams?: any,
    ): Promise<any> => {
      const res = await GetUserPayUrlV2({ order_no });

      if (res?.url) {
        await PayPageOpenStatus({
          order_no: res?.order_no || "",
          status: 1,
          mac: "",
        });
        resolveDepositResponse({
          ...res,
          ...(extraPrams && { ...extraPrams }),
        });
        return res;
      }

      // 如果没拿到 url，继续轮询，最多重试 5 次
      if (count >= 5) {
        return res;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      return repeatDeposit(order_no, count + 1);
    };

    let dialog_name,
      res,
      gameList,
      result,
      defaultSportGameId,
      telegramUrl,
      shareList,
      filterItem,
      pass,
      match,
      newActivityOpen,
      newActivityParamsStr,
      depositParams: number[],
      channel: any,
      props: any,
      payload: any;
    switch (click_id) {
      // 跳转外部链接
      case ClickType.ExternalLink:
        toOutLink(click_params as string);
        break;
      // 跳转页面
      case ClickType.Page:
        click_params = click_params as string;
        if (click_params && click_params.indexOf("sport") >= 0) {
          match = click_params.match(/\/sports\/(\d+)/);
          pass = await preCheckEnterGame({
            gid: `${match ? match[1] : ""}`,
          });
          if (!pass) {
            return;
          }
        }
        push(click_params as string);
        break;
      // 跳转弹窗，兼容线上，改为跳转路由弹窗页面
      case ClickType.Popup:
        click_params = click_params as string;
        if (click_params && click_params.indexOf("sport") >= 0) {
          match = click_params.match(/\/sports\/(\d+)/);
          pass = await preCheckEnterGame({
            gid: `${match ? match[1] : ""}`,
          });
          if (!pass) {
            return;
          }
        }
        push(
          `/${click_params.includes("#/") ? click_params.split("/")[1] : click_params}` as string,
        );
        break;
      // 跳转打开游戏
      case ClickType.Game:
        // 体育游戏
        defaultSportGameId = sysConfigStore.sport_config.sport_game_id;
        // 判断是否需要进入游戏
        if (noEntryGameList.includes(Number(click_params))) {
          toast.error(t("game.game_not_open"));
          return;
        }
        // 是否能进入游戏 - 预检
        pass = await preCheckEnterGame({
          gid: `${click_params}`,
        });
        if (!pass) {
          return;
        }
        // 判断是否体育游戏
        // 暂无法判断是否体育游戏，目前全局只有betby一个体育游戏
        if (Number(click_params) === Number(defaultSportGameId)) {
          push(`/sports/${click_params}`);
          return;
        }
        // 剩余的游戏按正常进入
        res = await reqGetGameList();
        gameList = res?.list || [];
        result = gameList.filter(
          (item) => item?.game_id === Number(click_params),
        );
        if (result.length !== 0) {
          push(`/game-detail/${result[0].game_id}`);
        } else {
          // 预检已拦截 errorCode: 1602
          toCasino(-1);
        }
        break;
      case ClickType.LiveChat:
        clickLiveChat();
        break;
      // 跳转内部文档
      case 6: {
        let url = click_params as string;
        if (!url.startsWith("http")) {
          url = `${ResConfig["files-address"]}${url}`;
        }
        handleOutLink(url);
        break;
      }
      case 7:
        window.scrollTo({
          top: click_params ? Number(click_params) : 1,
          behavior: "smooth",
        });
        break;
      // 打开内部弹窗
      case ClickType.InternalPopup:
        newActivityOpen =
          typeof click_params === "string"
            ? click_params?.indexOf("ActivityIframe") > -1
            : false;
        dialog_name = newActivityOpen
          ? "ActivityIframe"
          : (click_params as DialogType);
        newActivityParamsStr = newActivityOpen
          ? click_params?.split("?")[1]
          : "";
        if (!isMobile() && click_params === "AppDownloadActivity") {
          click(ClickType.Page as number, "/install");
          return;
        }
        if (!userStore.isLogin && click_params !== "ActivityIframe-13") {
          handleNoLogin();
          return;
        }
        showDialog({
          type: DialogType[dialog_name],
          data: {
            extra,
            atype: newActivityOpen
              ? Number(
                  typeof click_params === "string"
                    ? click_params?.match(/ActivityIframe-(\d+)/)?.[1]
                    : 0,
                )
              : "",
            params: newActivityParamsStr,
          },
        });
        break;
      // 跳转游戏列表
      case ClickType.GameList:
        push(`/game-list/${click_params}`);
        break;
      // 跳转文本弹窗
      case 10:
        break;
      // 打开聊天室
      case 11:
        break;
      // 跳转厂商游戏列表
      case 12:
        push(`/providers/${click_params}`);
        break;
      // 跳转PWA下载链接
      case ClickType.PWA:
        if (isMobile()) {
          showDialog({
            type: DialogType.ActivityIframe,
            data: { atype: ActivityTypeEnum.appdownload },
          });
        }
        break;
      // 跳转App下载链接
      case ClickType.APP:
        // 已与产品确认 跳转下载页面 不区分平台
        push(`/install`);
        break;
      // 跳转Tg群组
      case ClickType.Telegram:
        telegramUrl = getWebsiteConfigV2()?.telegramUrl;
        if (telegramUrl) {
          toOutLink(telegramUrl);
        } else {
          shareList = getShareListCfg();
          filterItem = shareList?.filter(
            (item: unknown) => item?.ipatype === ipatypeEnum.telegram,
          )[0];
          clickShare(filterItem);
        }
        break;
      case ClickType.AUTODeposit:
        // 金额、道具，渠道
        depositParams = click_params?.split(",");
        res = await GetUserPayChannelV2({
          point_type: sysConfigStore.point_type,
        });
        if (res) {
          // 没有活动
          // if (res?.discount?.activity_list) {
          // }
          // 没有渠道
          channel = res?.data?.filter(
            (item) => item?.id === `${depositParams?.[2]}`,
          );
          if (channel?.length === 0) {
            toDeposit();
            return;
          }
          // 没有道具item_id
          props = res?.discount?.promoitem_list
            ? res?.discount?.promoitem_list.filter(
                (item: any) => item?.cfg_id === Number(depositParams?.[1]),
              )
            : [];
          if (props?.length === 0) {
            toDeposit();
            return;
          }
          // USDT不拉单
          if (channel?.[0]?.pay_key === DepositKeyTypeEnum.usdt) {
            toDeposit();
            return;
          }
          if (
            !validateByRule(
              channel?.[0]?.input_param_rule,
              channel?.[0]?.pay_attach,
            ).pass
          ) {
            toDeposit();
            return;
          }
          payload = {
            channel_id: `${depositParams?.[2]}`,
            amount: `${Number(depositParams?.[0]) / channel?.[0]?.point_ratio}`,
            point: Number(depositParams?.[0]),
            point_type: channel?.[0]?.point_type,
            extra_id: 2,
            extra_param: `${props?.[0]?.item_id}`,
            pay_attach: {
              amount: `${Number(depositParams?.[0]) / channel?.[0]?.point_ratio}`,
              ...channel?.[0]?.pay_attach,
              ...(channel?.[0]?.input_param_rule?.identify_types && {
                identify_type:
                  channel?.[0]?.pay_attach?.identifys?.[0]?.identify_type,
                identify: channel?.[0]?.pay_attach?.identifys?.[0]?.identify,
              }),
            },
          };
          await KYCStore.getUserKycAuthByAction(
            UserKycAuthActionType.deposit,
            () =>
              UserApplyPayV2(payload, true).then(async (res) => {
                if (res?.user_auth) return res;
                if (res?.url) {
                  PayPageOpenStatus({
                    order_no: res?.order_no || "",
                    status: 1,
                    mac: "",
                  });
                  resolveDepositResponse({
                    ...res,
                    icon: channel?.[0]?.icon,
                    amount: `${Number(depositParams?.[0]) / channel?.[0]?.point_ratio}`,
                  });
                  return res;
                } else {
                  return await repeatDeposit(res?.order_no || "");
                }
              }) as any,
          );
        }
        break;
      default:
        break;
    }
  };

  return { click };
}
